import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

export type TourStepPlacement =
  | "auto"
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "center";

export interface RuntimeTour {
  id: string;
  audienceId: string;
  routePattern: string;
  title: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// P23a: a step targets exactly one of `targetId` (data-tour
// anchor) or `targetSelector` (manual CSS selector). The runtime
// resolves whichever is set; the server-side CHECK constraint
// guarantees exactly one is non-null for highlight steps.
// P23C: chapter steps have both null and stepType === "chapter".
export type PreAction = {
  kind: "click";
  selector: string;
  settleMs: number;
};

export interface RuntimeStep {
  id: string;
  tourId: string;
  sortOrder: number;
  // P23C: "highlight" (DOM-anchored) or "chapter" (centered modal card)
  stepType: "highlight" | "chapter";
  targetId: string | null;
  targetSelector: string | null;
  preAction: PreAction | null;
  placement: TourStepPlacement;
  title: string | null;
  body: string;
  mediaFileId: string | null;
  mediaUrl: string | null;
  skipBeacon: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ForRouteResponse {
  tour: RuntimeTour | null;
  steps: RuntimeStep[];
}

interface SeenStateEntry {
  seenAt: string | null;
  dismissedAt: string | null;
  lastStepIndex: number | null;
}

interface SeenStateResponse {
  state: Record<string, SeenStateEntry>;
}

export type TerminalKind = "seen" | "dismiss";

export interface UseTourReturn {
  tour: RuntimeTour | null;
  steps: RuntimeStep[];
  isRunning: boolean;
  hasBeenSeen: boolean;
  trigger: () => void;
  // P23C: launch a specific tour by ID (for the catalogue popover).
  // Sets a forced tour override and triggers immediately.
  triggerById: (tourId: string) => void;
  markCompleted: (kind: TerminalKind) => void;
  // Monotonic counter that bumps on every fresh start (autostart or
  // help-button trigger). TourManager uses it as the Joyride `key` so
  // each new run gets a fresh Joyride mount at step 0 — Joyride V3
  // otherwise persists its internal step index across `run` toggles
  // and would resume mid-tour after dismissal.
  runId: number;
}

const TOUR_AUTOSTART_DELAY_MS = 600;

// P23a/G1: callers pass the literal pathname (e.g. `/animals/42`).
// The server matches it against each tour's `route_pattern` via
// regexparam, removing the V1 hardcoded allowlist that previously
// only fired tours on `/dashboard`.
export function useTour(path: string | null): UseTourReturn {
  const [running, setRunning] = useState(false);
  const [userTriggered, setUserTriggered] = useState(false);
  // Local latch: once the tour terminates this route-visit, suppress
  // autostart even before the seen-query has refetched. Reset on route
  // change or on explicit user re-trigger.
  const [completedThisRoute, setCompletedThisRoute] = useState(false);
  const [runId, setRunId] = useState(0);
  // P23C: when set, forces a specific tour to load instead of the best-match.
  // Cleared on route change.
  const [forcedTourId, setForcedTourId] = useState<string | null>(null);

  useEffect(() => {
    setRunning(false);
    setUserTriggered(false);
    setCompletedThisRoute(false);
    setRunId(0);
    setForcedTourId(null);
  }, [path]);

  const forRouteUrl = useMemo(() => {
    if (!path) return null;
    const base = `/api/tours/for-route?path=${encodeURIComponent(path)}`;
    return forcedTourId ? `${base}&forceId=${encodeURIComponent(forcedTourId)}` : base;
  }, [path, forcedTourId]);

  const tourQuery = useQuery<ForRouteResponse>({
    queryKey: ["/api/tours/for-route", path, forcedTourId],
    enabled: !!path,
    queryFn: async () => {
      return (await apiRequest("GET", forRouteUrl!)) as ForRouteResponse;
    },
  });

  const seenQuery = useQuery<SeenStateResponse>({
    queryKey: ["/api/tours/seen"],
  });

  const tour = tourQuery.data?.tour ?? null;
  const steps = useMemo(() => tourQuery.data?.steps ?? [], [tourQuery.data]);
  const seenEntry = tour ? seenQuery.data?.state?.[tour.id] : undefined;
  const hasBeenSeen = !!(seenEntry?.seenAt || seenEntry?.dismissedAt);

  useEffect(() => {
    if (!tour || steps.length === 0) return;
    if (hasBeenSeen || userTriggered || completedThisRoute) return;
    if (running) return;
    const timer = setTimeout(() => {
      setRunning(true);
      setRunId((id) => id + 1);
    }, TOUR_AUTOSTART_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    tour,
    steps.length,
    hasBeenSeen,
    userTriggered,
    completedThisRoute,
    running,
  ]);

  const trigger = useCallback(() => {
    setUserTriggered(true);
    setCompletedThisRoute(false); // re-arm so a fresh terminal can latch again
    setRunning(true);
    // Bump runId so TourManager remounts Joyride from step 0. Without
    // this, re-triggering after a mid-tour dismiss resumes Joyride at
    // its persisted internal index, which is the dismissed step + 1.
    setRunId((id) => id + 1);
  }, []);

  // P23C: load a specific tour by ID and immediately start it.
  const triggerById = useCallback((tourId: string) => {
    setForcedTourId(tourId);
    setUserTriggered(true);
    setCompletedThisRoute(false);
    setRunning(true);
    setRunId((id) => id + 1);
  }, []);

  const markCompleted = useCallback(
    (kind: TerminalKind) => {
      // Latch first, *then* tear down + persist. Order matters: setting
      // the latch before clearing `running` closes the autostart window.
      setCompletedThisRoute(true);
      setRunning(false);
      // Clear the forced override so the next trigger() uses best-match again.
      setForcedTourId(null);
      if (!tour) return;
      const path = kind === "seen" ? "seen" : "dismiss";
      apiRequest("POST", `/api/tours/${tour.id}/${path}`)
        .then(() =>
          queryClient.invalidateQueries({ queryKey: ["/api/tours/seen"] }),
        )
        .catch(() => undefined);
    },
    [tour],
  );

  return {
    tour,
    steps,
    isRunning: running,
    hasBeenSeen,
    trigger,
    triggerById,
    markCompleted,
    runId,
  };
}
